// Top-level volumes live at `/volumes` (create/list/delete are service-scoped);
// attach/detach/resize/snapshot act through the owning instance. A volume's
// `id` is its image id; to resize/detach/snapshot an attached volume we map it
// to the instance disk whose image id matches, then use that disk's `disk_id`.
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from '../_shared';
import type { InstanceDisk } from '../instances';

/** Resolve the instance disk index (`disk_id`) backing a standalone volume,
 *  matching on the volume's image id. Needed for detach/resize/snapshot. */
export function resolveVolumeDiskId(
  disks: InstanceDisk[] | undefined,
  volumeId: string | undefined,
): string | undefined {
  if (!volumeId) return undefined;
  const d = disks?.find((x) => x.id === volumeId || x.image_id === volumeId || x.image === volumeId);
  return d?.disk_id;
}

export interface Volume {
  id?: string;
  name: string;
  /** Size in MB (display as `size / 1024` GB). */
  size: number;
  type: 'standard' | 'hiops' | 'nvme' | string;
  /** '1' when the volume is persistent. */
  persistent?: string;
  fstype?: string;
  state?: string;
  running_vms?: string;
  attached?: { name: string; instance_id: string };
}

export function useVolumes(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.volumes(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ volumes?: Volume[] } | Volume[]>(
        await serviceApi.get(onPath(serviceId!, 'volumes')),
      );
      return Array.isArray(body) ? body : body.volumes ?? [];
    },
  });
}

export interface CreateVolumePayload {
  name: string;
  size: number;
  type: Volume['type'];
}

/** Selectable volume types (shared by the volume create + attach-create forms). */
export const VOLUME_TYPES: { value: Volume['type']; label: string }[] = [
  { value: 'hiops', label: 'High IOPS SSD' },
  { value: 'standard', label: 'Standard' },
  { value: 'nvme', label: 'NVMe SSD' },
];

export function useCreateVolume(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, CreateVolumePayload>,
) {
  const qc = useQueryClient();
  return useMutation({
    // Backend `create_volume_params` does `params.require(:volume)` → the fields
    // must be nested under a `volume` key (flat payload 400s).
    mutationFn: async (payload: CreateVolumePayload) =>
      (await serviceApi.post(onPath(serviceId, 'volumes'), { volume: payload })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.volumes(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteVolume(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (volumeId: string) =>
      (await serviceApi.delete(onPath(serviceId, `volumes/${volumeId}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.volumes(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

/** Attach a standalone volume to an instance (`disk_id` payload === volume id). */
export function useAttachVolume(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, { instanceId: string; volumeId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ instanceId, volumeId }: { instanceId: string; volumeId: string }) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/volumes/${volumeId}/attach`), {
        disk_id: volumeId,
      })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.volumes(serviceId) });
      // Reload the target instance so its disks list reflects the new attachment.
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, a[1].instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

/** Detach a standalone volume (path keeps the volume id; body carries the disk index). */
export function useDetachVolume(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, { instanceId: string; volumeId: string; diskId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ instanceId, volumeId, diskId }) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/volumes/${volumeId}/detach`), {
        disk_id: diskId,
      })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.volumes(serviceId) });
      // Reload the source instance so its disks list drops the detached volume.
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, a[1].instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useResizeDisk(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, { disk_id: string; size: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ disk_id, size }: { disk_id: string; size: number }) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/volumes/${disk_id}/resize`), { size }))
        .data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDetachDisk(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, { volumeId: string; diskId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    // Path carries the volume (image) id that `resolve_volume_uid` looks up; the
    // disk index to unplug goes in the `disk_id` body (backend reads
    // `params[:disk_id]`). Passing the disk index in the path 404s.
    mutationFn: async ({ volumeId, diskId }: { volumeId: string; diskId: string }) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/volumes/${volumeId}/detach`), {
        disk_id: diskId,
      })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      // The freed volume becomes standalone again — refresh the volumes list too.
      qc.invalidateQueries({ queryKey: onKeys.volumes(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}
